// DreamDev wordmark + icon, copied from dreamdev.io. Paths use
// `currentColor` so callers control the color via CSS (or the `color`
// prop). Two variants:
//   - "lockup" (default): full "DreamDev" wordmark + icon (389×70)
//   - "mark": just the icon glyph (52×70), useful in small chips
import React from 'react';

interface Props {
  variant?: 'lockup' | 'mark';
  height?: number;
  color?: string;
  title?: string;
  className?: string;
}

const DreamDevLogo: React.FC<Props> = ({
  variant = 'lockup', height = 22, color, title = 'DreamDev', className,
}) => {
  const isMark = variant === 'mark';
  const viewBox = isMark ? '0 0 53 70' : '0 0 389 70';
  const aspect = isMark ? 53 / 70 : 389 / 70;
  const width = Math.round(height * aspect);

  return (
    <svg
      viewBox={viewBox}
      width={width}
      height={height}
      role="img"
      aria-label={title}
      className={className}
      style={color ? { color } : undefined}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      {/* Icon glyph (D-mark) */}
      <g transform="translate(0.423 0.753)">
        <path
          d="M 30.456 5.169 L 21.5 0 L 21.5 10.345 C 21.5 11.404 21.078 12.421 20.328 13.17 L 1.172 32.284 C 0.422 33.034 0 34.051 0 35.11 L 0 51.883 C 0 55.233 3.626 57.326 6.524 55.65 L 30.456 41.832 C 31.693 41.119 32.453 39.8 32.453 38.375 L 32.453 8.623 C 32.453 7.198 31.693 5.879 30.456 5.167 Z"
          fill="currentColor"
          transform="translate(19.404 12.76)"
        />
        <path
          d="M 32.453 19.6 L 32.453 4.357 C 32.453 1.007 28.828 -1.086 25.929 0.59 L 1.997 14.408 C 0.761 15.121 0 16.439 0 17.865 L 0 47.617 C 0 49.042 0.761 50.361 1.997 51.073 L 10.953 56.243 L 10.953 44.366 C 10.953 43.306 11.376 42.289 12.125 41.54 L 31.281 22.426 C 32.031 21.677 32.453 20.66 32.453 19.6 Z"
          fill="currentColor"
        />
      </g>
      {!isMark && (
        <g transform="translate(76.281 14.009)" fill="currentColor">
          <path
            d="M 25.258 6.242 C 23.594 3.148 20.386 0 13.846 0 C 4.098 0 0 7.132 0 15.573 C 0 24.014 4.103 31.265 13.846 31.265 C 20.511 31.16 23.134 27.402 24.658 23.464 L 27.042 23.464 L 25.253 28.826 L 25.253 30.671 L 35.416 30.671 L 35.416 0.595 L 25.253 0.595 L 25.253 6.242 Z M 17.773 22.289 C 12.782 22.289 10.168 20.21 10.168 15.573 C 10.168 10.935 12.782 8.976 17.773 8.976 C 22.765 8.976 25.203 10.995 25.203 15.573 C 25.203 20.15 22.71 22.289 17.773 22.289 Z"
            transform="translate(104.213 11.235)"
          />
          <path
            d="M 39.807 0.005 C 33.861 0.005 30.479 2.974 28.815 6.662 C 27.655 2.774 25.242 0.14 20.49 0.015 L 20.49 0.005 C 20.44 0.005 20.39 0.01 20.335 0.01 C 20.271 0.01 20.206 0.005 20.141 0.005 L 20.141 0.02 C 14.51 0.17 11.916 3.743 10.752 8.981 L 8.259 8.981 L 10.162 3.273 L 10.162 0.6 L 0 0.6 L 0 30.676 L 10.162 30.676 L 10.162 20.45 C 10.162 13.259 11.112 8.976 16.103 8.976 L 16.283 8.976 C 19.431 8.976 20.325 10.46 20.325 16.107 L 20.325 30.491 L 20.266 30.671 L 30.488 30.671 L 30.488 20.565 C 30.488 13.074 31.378 8.976 36.609 8.976 C 39.757 8.976 40.651 10.465 40.651 16.107 L 40.651 30.671 L 50.814 30.671 L 50.814 15.513 C 50.814 6.777 48.616 0 39.822 0 Z"
            transform="translate(144.511 11.23)"
          />
          <path d="M 16.044 0 L 0 0 L 0 41.905 L 16.044 41.905 C 27.516 41.905 39.398 38.992 39.398 20.92 C 39.398 2.849 27.516 0 16.044 0 Z M 16.044 32.395 L 10.813 32.395 L 10.813 9.451 L 16.044 9.451 C 23.174 9.451 28.585 9.69 28.585 20.925 C 28.585 32.16 23.234 32.4 16.044 32.4 Z" />
          <path
            d="M 0 5.173 L 0 30.081 L 10.223 30.081 L 10.223 8.976 L 25.438 8.976 L 25.438 0 L 5.292 0 C 1.904 0 0.005 1.784 0.005 5.173 Z"
            transform="translate(41.951 11.824)"
          />
          <path
            d="M 16.464 23.594 C 12.781 23.594 10.578 21.515 10.223 17.772 L 32.388 17.772 L 32.388 15.098 C 32.388 5.647 26.387 0 15.984 0 C 6.655 0 0 5.822 0 15.513 C 0 25.203 6.301 31.265 16.464 31.265 C 25.732 31.265 31.618 26.688 32.448 20.031 L 22.345 20.031 C 21.75 22.349 19.611 23.594 16.464 23.594 Z M 16.284 7.372 C 19.851 7.372 22.165 9.211 22.464 12.544 L 10.223 12.544 C 10.463 9.216 12.781 7.372 16.284 7.372 Z"
            transform="translate(68.508 11.235)"
          />
          <path
            d="M 16.044 0 L 0 0 L 0 41.905 L 16.044 41.905 C 27.511 41.905 39.398 38.992 39.398 20.92 C 39.398 2.849 27.511 0 16.044 0 Z M 16.044 32.395 L 10.813 32.395 L 10.813 9.451 L 16.044 9.451 C 23.174 9.451 28.585 9.69 28.585 20.925 C 28.585 32.16 23.239 32.4 16.044 32.4 Z"
            transform="translate(200.117 0)"
          />
          <path
            d="M 16.463 23.594 C 12.781 23.594 10.578 21.515 10.223 17.772 L 32.388 17.772 L 32.388 15.098 C 32.388 5.647 26.387 0 15.984 0 C 6.655 0 0 5.822 0 15.513 C 0 25.203 6.3 31.265 16.463 31.265 C 25.732 31.265 31.618 26.688 32.447 20.031 L 22.344 20.031 C 21.75 22.349 19.611 23.594 16.463 23.594 Z M 16.289 7.372 C 19.856 7.372 22.17 9.211 22.469 12.544 L 10.228 12.544 C 10.468 9.216 12.786 7.372 16.289 7.372 Z"
            transform="translate(242.773 11.235)"
          />
          <path
            d="M 25.792 0 L 18.127 27.102 L 10.283 0 L 0 0 L 8.799 30.076 L 27.041 30.076 L 35.775 0 Z"
            transform="translate(276.225 11.829)"
          />
        </g>
      )}
    </svg>
  );
};

export default DreamDevLogo;
